import type { GaborGrangerAnswer, GaborGrangerLadderState } from "../types";

// Brackets the switch point, with -1 / `priceCount` as the open bounds. Derived
// from the full answer set, so a self-contradicting respondent yields a crossed
// bracket that reads as done instead of looping.
export const computeLadderState = ({
  priceCount,
  answers,
}: {
  priceCount: number;
  answers: GaborGrangerAnswer[];
}): GaborGrangerLadderState => {
  let maxAccepted = -1;
  let minDeclined = priceCount;

  for (const answer of answers) {
    if (answer.accepted) {
      maxAccepted = Math.max(maxAccepted, answer.priceIndex);
    } else {
      minDeclined = Math.min(minDeclined, answer.priceIndex);
    }
  }

  return { maxAccepted, minDeclined };
};

// True when every accepted price sits below every declined one. A crossed bracket
// is a data-quality signal worth surfacing, not silently modelling.
export const isMonotonic = (answers: GaborGrangerAnswer[]): boolean => {
  const { maxAccepted, minDeclined } = computeLadderState({
    // priceCount only bounds minDeclined's default, which cannot cross a real
    // accepted index, so any value at or above the answered range works here.
    priceCount: Number.MAX_SAFE_INTEGER,
    answers,
  });
  return maxAccepted < minDeclined;
};

// Next price index, or `undefined` when finished. `startIndex` applies only to the
// first rung; the rest are a pure function of the answers, so a resumed interview
// recomputes its position with no stored pointer.
export const getNextPriceIndex = ({
  priceCount,
  startIndex,
  answers,
}: {
  priceCount: number;
  startIndex: number;
  answers: GaborGrangerAnswer[];
}): number | undefined => {
  if (priceCount <= 0) return undefined;
  if (answers.length === 0) {
    return Math.min(Math.max(startIndex, 0), priceCount - 1);
  }

  const { maxAccepted, minDeclined } = computeLadderState({
    priceCount,
    answers,
  });
  if (maxAccepted + 1 >= minDeclined) return undefined;

  // Step toward the bound the last answer moved: accepting probes higher,
  // declining probes lower. Both land strictly inside the open bracket, so
  // neither can re-ask an answered price.
  const lastAnswer = answers[answers.length - 1];
  return lastAnswer.accepted ? maxAccepted + 1 : minDeclined - 1;
};

// Highest price index they would buy at. `-1` means they declined even the cheapest,
// a real answer that belongs in the demand-curve denominator rather than an error.
// `undefined` means inconsistent, so no switch point represents them.
export const getSwitchPointIndex = (
  answers: GaborGrangerAnswer[]
): number | undefined => {
  if (!isMonotonic(answers)) return undefined;
  const { maxAccepted } = computeLadderState({
    priceCount: Number.MAX_SAFE_INTEGER,
    answers,
  });
  return maxAccepted;
};

// Undefined until the answers pin the switch point down, which is exactly when
// extraction keeps it, so readers never name a price the curve dropped.
export const getEstablishedSwitchPointIndex = ({
  answers,
  priceCount,
}: {
  answers: GaborGrangerAnswer[];
  priceCount: number;
}): number | undefined => {
  const switchPointIndex = getSwitchPointIndex(answers);
  if (switchPointIndex === undefined) return undefined;

  const isEstablished =
    switchPointIndex < 0
      ? answers.some((a) => a.priceIndex === 0 && !a.accepted)
      : switchPointIndex === priceCount - 1 ||
        answers.some(
          (a) => a.priceIndex === switchPointIndex + 1 && !a.accepted
        );

  return isEstablished ? switchPointIndex : undefined;
};
